import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import formatCurrency from '../../../lib/formatCurrency';
import DeliveryInfo from '../DeliveryInfo';
import {
  ExpandedSection,
  ExpandedGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  PriceAccordion,
  PriceAccordionHeader,
  PriceAccordionContent,
  LinksRow,
  ExternalLink,
  ViewDetailsButton,
  ProductInfoTags,
  Tag,
} from './ExpandedDetails.style';

const ExpandedDetails = ({
  isOpen,
  tyre,
  onDeliveryDateChange,
  onRefreshDelivery,
  onViewDetails,
}) => {
  const {t} = useTranslation();
  const [showAdditionalPrices, setShowAdditionalPrices] = useState(false);

  const productInfoArray = tyre.productInfo ? tyre.productInfo.split('|') : [];
  const profileDepth = tyre.depth ?? tyre.treadDepth;

  return (
    <ExpandedSection isOpen={isOpen}>
      <DeliveryInfo
        tyre={tyre}
        onDeliveryDateChange={onDeliveryDateChange}
        onRefreshDelivery={onRefreshDelivery}
      />

      {(tyre.netPrice || tyre.discountGrossPrice) && (
        <PriceAccordion>
          <PriceAccordionHeader
            onClick={() => setShowAdditionalPrices(!showAdditionalPrices)}
          >
            <i className="mdi mdi-eye" />
            {showAdditionalPrices ? t('hideOtherData') : t('showOtherData')}
            <i
              className="mdi mdi-chevron-down chevron"
              style={{
                transform: showAdditionalPrices
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
              }}
            />
          </PriceAccordionHeader>
          <PriceAccordionContent isOpen={showAdditionalPrices}>
            {tyre.netPrice && (
              <InfoCard>
                <InfoLabel>{t('netPrice')}</InfoLabel>
                <InfoValue>{formatCurrency(tyre.netPrice)}</InfoValue>
              </InfoCard>
            )}
            {tyre.discountGrossPrice && (
              <InfoCard>
                <InfoLabel>{t('discountGross')}</InfoLabel>
                <InfoValue>{formatCurrency(tyre.discountGrossPrice)}</InfoValue>
              </InfoCard>
            )}
          </PriceAccordionContent>
        </PriceAccordion>
      )}
      <ExpandedGrid>
        {profileDepth !== undefined && Number(profileDepth) > 0 && (
          <InfoCard>
            <InfoLabel>{t('profileDepth')}</InfoLabel>
            <InfoValue>{profileDepth} mm</InfoValue>
          </InfoCard>
        )}
        {tyre.loadRating && (
          <InfoCard>
            <InfoLabel>{t('loadRating')}</InfoLabel>
            <InfoValue>{tyre.loadRating}</InfoValue>
          </InfoCard>
        )}
      </ExpandedGrid>

      {productInfoArray.length > 0 && (
        <ProductInfoTags>
          {productInfoArray.map((tag, index) => (
            <Tag key={index}>{tag.trim()}</Tag>
          ))}
        </ProductInfoTags>
      )}

      <LinksRow>
        {tyre.efficiencySticker && (
          <ExternalLink
            href={tyre.efficiencySticker}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="mdi mdi-label" />
            {t('energyLabel')}
          </ExternalLink>
        )}
        {tyre.erpel && (
          <ExternalLink
            href={tyre.erpel}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="mdi mdi-qrcode" />
            EPREL
          </ExternalLink>
        )}
        <ViewDetailsButton onClick={onViewDetails}>
          <i className="mdi mdi-eye" />
          {t('viewDetails')}
        </ViewDetailsButton>
      </LinksRow>
    </ExpandedSection>
  );
};

ExpandedDetails.propTypes = {
  isOpen: PropTypes.bool,
  tyre: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    netPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discountGrossPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    depth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treadDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loadRating: PropTypes.string,
    productInfo: PropTypes.string,
    efficiencySticker: PropTypes.string,
    erpel: PropTypes.string,
    deliveryDate: PropTypes.string,
    selectedDeliveryDate: PropTypes.string,
    scheduleDetails: PropTypes.array,
    errorMessage: PropTypes.string,
    infoLoading: PropTypes.bool,
  }).isRequired,
  onDeliveryDateChange: PropTypes.func,
  onRefreshDelivery: PropTypes.func,
  onViewDetails: PropTypes.func.isRequired,
};

export default ExpandedDetails;
